// Category cards updated with tighter spacing and four balanced desktop columns.
// Updated: mobile now uses a 2x2 grid (no horizontal scroll) with smaller cards
//   and more padding so the section fits within 100dvh.
// Updated: reduced mobile section spacing, image scale, and card footer height.
// Updated: the "Onze werelden" title is hidden on mobile to preserve vertical space.
// Updated: added lazy/async decoding and intrinsic dimensions so category images
//   no longer block the page load and don't shift layout.

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const categories = [
  {
    name: 'Haagplanten',
    href: '/haagplanten',
    image: '/assets/categories/haagplanten.webp',
  },
  {
    name: 'Bomen',
    href: '/bomen',
    image: '/assets/categories/bomen.webp',
  },
  {
    name: 'Tuinplanten',
    href: '/tuinplanten',
    image: '/assets/categories/tuinplanten.webp',
  },
  {
    name: 'Aanplant en verzorging',
    href: '/aanplant-en-verzorging',
    image: '/assets/categories/aanplantservice.webp',
  },
];

const CategoryCards = () => {
  return (
    <section data-fast-scroll="viewed_categories" className="w-full bg-[#fbf9f8] py-2 sm:py-5">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          viewport={{ once: true }}
          className="mb-0 flex items-end justify-between gap-6 sm:mb-4"
        >
          <div>
            <h2 className="hidden text-3xl tracking-[-0.035em] text-[#1a1a1a] sm:block sm:text-4xl md:text-5xl">Onze werelden</h2>
          </div>
          <a
            href="/assortiment"
            className="group hidden items-center gap-2 text-sm font-medium text-[#1a1a1a] transition-colors duration-200 hover:text-[#5a6552] sm:inline-flex"
          >
            Bekijk alle categorieën
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
          </a>
        </motion.div>

        <div className="grid grid-cols-2 gap-2 px-1 pb-1 sm:mx-0 sm:gap-5 sm:px-0 sm:pb-0 lg:grid-cols-4">
          {categories.map((category, index) => (
            <motion.a
              key={category.name}
              href={category.href}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.08, ease: 'easeOut' }}
              viewport={{ once: true }}
              className="group flex flex-col overflow-hidden rounded-xl border border-transparent bg-white transition-[box-shadow] duration-200 hover:ring-2 hover:ring-[#1a1a1a] hover:ring-offset-[3px] hover:ring-offset-[#fbf9f8]"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={category.image}
                  alt={category.name}
                  width={800}
                  height={500}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex min-h-[36px] items-center justify-between gap-2 border-t border-gray-100 bg-white px-2 py-2 sm:min-h-[44px] sm:px-4 sm:py-3">
                <span className="break-words text-[10px] font-medium leading-snug text-[#1a1a1a] sm:text-[13px]">
                  {category.name}
                </span>
                <ArrowRight className="h-3 w-3 shrink-0 text-[#1a1a1a]/50 transition-colors duration-200 group-hover:text-[#1a1a1a]" aria-hidden="true" />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CategoryCards;
